import React, { useState, useRef, useEffect } from 'react';
import { Search, Sparkles, Globe, Loader2, FolderTree, ChevronLeft } from 'lucide-react';
import { Input } from './ui/input';
import { searchChemicals, findChemical } from '../data/chemicalDatabase';
import { CHEMICAL_CATEGORIES } from '../data/chemicalDatabaseExpanded';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChemicalAutocomplete({ value, onSelect, onChange, placeholder = 'اسم المادة أو رقم UN أو CAS أو عائلة...' }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [webError, setWebError] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [lastSources, setLastSources] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInput = (val) => {
    onChange(val);
    setWebError('');
    setActiveCategory(null);
    if (val.trim()) {
      setResults(searchChemicals(val, 20));
      setOpen(true);
    } else {
      setResults(searchChemicals('', 12));
      setOpen(true);
    }
  };

  const handlePick = (chem) => {
    onSelect(chem);
    setOpen(false);
  };

  const performWebSearch = async () => {
    const q = (value || '').trim();
    if (!q) return;
    setSearching(true);
    setWebError('');
    setLastSources([]);
    try {
      const res = await axios.post(`${API}/chemical-lookup`, { query: q }, { timeout: 45000 });
      const data = res.data;
      if (data && data.found !== false) {
        if (data.sources) setLastSources(data.sources);
        handlePick(data);
      } else {
        setWebError('لم يتم العثور على المادة في أي من المصادر السبعة');
      }
    } catch (e) {
      setWebError(e?.response?.data?.detail || 'تعذر البحث عبر الإنترنت');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
        <Input
          value={value || ''}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => {
            setResults(searchChemicals(value || '', 8));
            setOpen(true);
          }}
          placeholder={placeholder}
          className="text-sm h-9 pr-7"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-y-auto" data-testid="chemical-autocomplete-panel">
          <div className="px-2 py-1 text-[10px] text-slate-500 bg-slate-50 border-b flex items-center justify-between gap-1 sticky top-0 z-10">
            <div className="flex items-center gap-1">
              <Sparkles size={10} className="text-emerald-500" />
              ابحث بالاسم العربي/الإنجليزي أو رقم UN/CAS
            </div>
            {!activeCategory && (
              <button
                type="button"
                onClick={() => setActiveCategory('__list__')}
                className="text-[10px] text-[#1a4d80] hover:underline flex items-center gap-1"
                data-testid="browse-categories-btn"
              >
                <FolderTree size={10}/> تصفّح حسب العائلة
              </button>
            )}
            {activeCategory && (
              <button
                type="button"
                onClick={() => { setActiveCategory(null); setResults(searchChemicals(value || '', 12)); }}
                className="text-[10px] text-[#1a4d80] hover:underline flex items-center gap-1"
                data-testid="back-to-search-btn"
              >
                <ChevronLeft size={10}/> رجوع للبحث
              </button>
            )}
          </div>

          {/* Categories list */}
          {activeCategory === '__list__' && (
            <div className="p-1.5 grid grid-cols-2 gap-1" data-testid="category-grid">
              {CHEMICAL_CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => { setActiveCategory(cat.key); setResults(cat.items); }}
                  className="text-right px-2 py-2 rounded border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all"
                  data-testid={`category-${cat.key}`}
                >
                  <div className="text-[12px] font-bold text-[#1a4d80]">{cat.labelAr}</div>
                  <div className="text-[10px] text-slate-500">{cat.count} مادة</div>
                </button>
              ))}
            </div>
          )}

          {/* Chemical items */}
          {activeCategory !== '__list__' && results.map((chem, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handlePick(chem)}
              className="w-full text-right px-2 py-1.5 hover:bg-emerald-50 transition-colors border-b border-slate-100 last:border-0"
              data-testid={`autocomplete-item-${i}`}
            >
              <div className="font-bold text-[13px] text-[#1a4d80]">{chem.name}</div>
              <div className="text-[11px] text-slate-600 flex items-center gap-1 flex-wrap">
                {chem.chemicalName && <span className="text-slate-500">{chem.chemicalName}</span>}
                {chem.chemicalName && <span>•</span>}
                <span className="font-mono">{chem.unNumber}</span>
                <span>•</span>
                <span className="truncate">{chem.usage}</span>
              </div>
            </button>
          ))}

          {/* Web search fallback */}
          {activeCategory !== '__list__' && (value || '').trim() && (
            <div className="border-t border-slate-200 bg-slate-50/60 p-2">
              {results.length === 0 && (
                <div className="text-[11px] text-slate-600 mb-1.5">
                  لا توجد نتائج محلية لـ <strong>&quot;{value}&quot;</strong>
                </div>
              )}
              <button
                type="button"
                onClick={performWebSearch}
                disabled={searching}
                className="w-full flex items-center justify-center gap-2 bg-[#1a4d80] hover:bg-[#143d68] disabled:opacity-60 text-white text-[12px] font-bold py-1.5 rounded transition-colors"
                data-testid="web-search-btn"
              >
                {searching ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    جارٍ البحث في 7 مصادر دولية (PubChem · CAMEO · NIOSH · ECHA · ERG · Sigma · Fisher)...
                  </>
                ) : (
                  <>
                    <Globe size={14} />
                    البحث في 7 مصادر دولية + الذكاء الاصطناعي
                  </>
                )}
              </button>
              {lastSources.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1 justify-center" data-testid="source-badges">
                  {lastSources.map((s) => (
                    <span key={s} className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded uppercase font-bold">
                      ✓ {s}
                    </span>
                  ))}
                </div>
              )}
              {webError && (
                <div className="text-[11px] text-red-600 mt-1.5 text-center">{webError}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { findChemical };
